import React, { useEffect, useRef, useState } from 'react'
import Helmet from 'react-helmet'
import { Route, Switch } from 'react-router'
import { Link, matchPath, useLocation, useRouteMatch, withRouter } from 'react-router-dom'

import Slide from '@material-ui/core/Slide'
import { orderBy } from 'lodash'

import CaseDocumentsTab from '../components/case/CaseDetails/CaseDocumentsTab'
import CaseMessagesTab from '../components/case/CaseDetails/CaseMessagesTab'
import DetailsPage from '../components/case/CaseDetails/DetailsPage'
import TimelineTab from '../components/case/CaseDetails/TimelineTab'
import CaseNavigationBar from '../components/case/CaseNavigationBar'
import OverviewPage from '../components/case/OverviewPage'
import ReportsPage from '../components/case/ReportsPage'
import ErrorBoundary from '../components/controls/ErrorBoundary'
import FAIcon from '../components/FAIcon'
import AppointmentService from '../services/AppointmentsService'
import CasesService from '../services/CasesService'
import TasksService from '../services/TasksService'

import '../components/common/Skeleton.css'
import './CasePage.scss'

const CasePage = withRouter(({ caseNumber, user, history, settings }) => {
  const { path, url } = useRouteMatch()
  const returnToParam = new URLSearchParams(useLocation().search).get('returnTo')
  const saveFormRef = useRef(null)
  const [returnToUrl] = useState(returnToParam)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorStatus, setErrorStatus] = useState(null)
  const [client, setClient] = useState()
  const [caseStatus, setCaseStatus] = useState()
  const [allowedReportTypes, setAllowedReportTypes] = useState()
  const [caseTypeKey, setCaseTypeKey] = useState()
  const [disableFileReport, setDisableFileReport] = useState(false)
  const [incompleteAppointments, setIncompleteAppointments] = useState()
  const [allAppointments, setAllAppointments] = useState()
  const [incompleteTasks, setIncompleteTasks] = useState()
  const [completeTasks, setCompleteTasks] = useState()
  const [isDirty, setIsDirty] = useState(false)

  const tabBarScrollRef = useRef(null)
  const [showLeftScrollFade, setShowLeftScrollFade] = useState(false)
  const [showRightScrollFade, setShowRightScrollFade] = useState(false)

  const updateScrollFades = () => {
    const el = tabBarScrollRef.current
    if (el) {
      const showLeft = el.scrollLeft > 5
      const showRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 5
      setShowLeftScrollFade(showLeft)
      setShowRightScrollFade(showRight)
    }
  }

  useEffect(() => {
    updateScrollFades()
    window.addEventListener('resize', updateScrollFades)
    return () => {
      window.removeEventListener('resize', updateScrollFades)
    }
  }, [isLoading])

  useEffect(() => {
    const el = tabBarScrollRef.current
    if (!el) return

    const handleWheel = e => {
      if (el.scrollWidth > el.clientWidth) {
        e.preventDefault()
        el.scrollLeft += e.deltaY
        updateScrollFades()
      }
    }

    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      el.removeEventListener('wheel', handleWheel)
    }
  }, [isLoading])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const tabParam = searchParams.get('tab')
    if (tabParam) {
      searchParams.delete('tab')
      const rest = searchParams.toString()
      const queryStr = rest ? `?${rest}` : ''
      history.replace(`${url}/${tabParam.toLowerCase()}${queryStr}`)
    }
  }, [history, url])

  const tabs = [
    {
      name: 'Overview',
      link: `${url}`,
      pathProps: { path, exact: true },
      component: OverviewPage,
    },
    {
      name: 'Details',
      link: `${url}/details`,
      needsSave: true,
      pathProps: { path: `${path}/details` },
      component: DetailsPage,
    },
    {
      name: 'Timeline',
      link: `${url}/timeline`,
      pathProps: {
        path: [`${path}/timeline`, `${path}/tasks/:taskId?`, `${path}/appointments/:appointmentNumber?`],
      },
      component: TimelineTab,
    },
    {
      name: 'Reports',
      link: `${url}/reports`,
      pathProps: {
        path: [`${path}/reports`, `${path}/reports/in-progress`, `${path}/reports/:reportNumber`],
      },
      component: ReportsPage,
    },
    {
      name: 'Documents',
      link: `${url}/documents`,
      pathProps: { path: `${path}/documents` },
      component: CaseDocumentsTab,
    },
    {
      name: 'Messages',
      link: `${url}/messages`,
      pathProps: { path: `${path}/messages` },
      component: CaseMessagesTab,
    },
  ]
  const selectedTabIndex = tabs.findIndex(x => {
    const matchedPath = matchPath(window.location.pathname, x.pathProps)
    return matchedPath !== null
  })
  const selectedTab = tabs[selectedTabIndex]
  const getTabClass = tabIndex => (tabIndex === selectedTabIndex ? 'active-tab-item' : 'tab-items')

  useEffect(() => {
    let isMounted = true
    setError(null)
    setErrorStatus(null)
    setIsLoading(true)

    CasesService.get(caseNumber, [
      'client',
      'caseStatus',
      'intakeComplete',
      'allowedReportTypes',
      'caseTypeKey',
      'disableFileReport',
    ])
      .then(r => {
        if (!isMounted) return
        setClient(r.client)
        setCaseStatus(r.caseStatus)
        setAllowedReportTypes(r.allowedReportTypes)
        setCaseTypeKey(r.caseTypeKey)
        setDisableFileReport(r.disableFileReport)

        return Promise.all([
          AppointmentService.getCaseAppointments(caseNumber, 'complete eq 0')
            .then(appts => {
              if (isMounted) setIncompleteAppointments(appts ? orderBy(appts, x => x.startUtc) : [])
            })
            .catch(() => {
              if (isMounted) setIncompleteAppointments([])
            }),
          AppointmentService.getCaseAppointments(caseNumber)
            .then(appts => {
              if (isMounted) setAllAppointments(appts ? orderBy(appts, x => x.startUtc, 'desc') : [])
            })
            .catch(() => {
              if (isMounted) setAllAppointments([])
            }),
          TasksService.getAllForCase(caseNumber, 'complete eq 0')
            .then(tasks => {
              if (isMounted) setIncompleteTasks(tasks ? orderBy(tasks, x => x.createdUtc) : [])
            })
            .catch(() => {
              if (isMounted) setIncompleteTasks([])
            }),
          TasksService.getAllForCase(caseNumber, 'complete eq 1')
            .then(tasks => {
              if (isMounted) setCompleteTasks(tasks ? orderBy(tasks, x => x.createdUtc) : [])
            })
            .catch(() => {
              if (isMounted) setCompleteTasks([])
            }),
        ])
      })
      .catch(err => {
        if (!isMounted) return
        setError(err.message || 'Unable to retrieve case.')
        setErrorStatus(
          err.status ||
            (err.message && err.message.includes('403')
              ? 403
              : err.message && err.message.includes('404')
              ? 404
              : null),
        )
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [caseNumber])

  const refreshCaseStatus = async () => {
    const r = await CasesService.get(caseNumber, [
      'client',
      'caseStatus',
      'intakeComplete',
      'allowedReportTypes',
      'caseTypeKey',
      'disableFileReport',
    ])
    if (r.client) setClient(r.client)
    setCaseStatus(r.caseStatus)
    setAllowedReportTypes(r.allowedReportTypes)
    setCaseTypeKey(r.caseTypeKey)
    setDisableFileReport(r.disableFileReport)

    // Re-fetch tasks and appointments — needed when auto-generated tasks are created on activation or updates
    const [incomplete, complete, appointments, allCaseAppts] = await Promise.all([
      TasksService.getAllForCase(caseNumber, 'complete eq 0'),
      TasksService.getAllForCase(caseNumber, 'complete eq 1'),
      AppointmentService.getCaseAppointments(caseNumber, 'complete eq 0'),
      AppointmentService.getCaseAppointments(caseNumber).catch(() => null),
    ])
    setIncompleteTasks(incomplete ? orderBy(incomplete, x => x.createdUtc) : [])
    setCompleteTasks(complete ? orderBy(complete, x => x.createdUtc) : [])
    setIncompleteAppointments(appointments ? orderBy(appointments, x => x.startUtc) : [])
    if (allCaseAppts) {
      setAllAppointments(orderBy(allCaseAppts, x => x.startUtc, 'desc'))
    }
  }

  useEffect(() => {
    const handleNotification = e => {
      const data = e.detail
      if (
        data &&
        (data.title === 'Refresh Complete' ||
          data.title === 'Smart Upload Complete' ||
          data.title === 'Smart Intake Complete' ||
          data.title === 'Smart Document Review Complete')
      ) {
        const match = data.deepLink?.match(/\/cases\/(\d+)/)
        const notificationCaseNumber = match ? parseInt(match[1], 10) : null
        if (notificationCaseNumber === parseInt(caseNumber, 10)) {
          refreshCaseStatus()
        }
      }
    }

    window.addEventListener('app-notification', handleNotification)
    return () => {
      window.removeEventListener('app-notification', handleNotification)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber])

  const handleClickExit = e => {
    if (e && e.preventDefault) {
      e.preventDefault()
    }

    if (returnToUrl) {
      history.push(returnToUrl)
      return
    }

    const lastNonCaseUrl = sessionStorage.getItem('lastNonCaseUrl')
    if (lastNonCaseUrl) {
      history.push(lastNonCaseUrl)
    } else {
      history.push('/cases')
    }
  }

  const handleTaskDeleted = taskNumber => {
    setIncompleteTasks(prev => (prev || []).filter(t => t.taskNumber !== taskNumber))
  }

  const triggerFormSubmission = () => {
    if (saveFormRef.current.requestSubmit) {
      saveFormRef.current.requestSubmit()
    } else {
      saveFormRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
    }
  }

  const is404 =
    errorStatus === 404 || (error && error.includes('404')) || (error && error.toLowerCase().includes('not found'))
  const is403 =
    errorStatus === 403 ||
    (error && error.includes('403')) ||
    (error && error.toLowerCase().includes('forbidden')) ||
    (error && error.toLowerCase().includes('permission'))
  const hasFatalError = !isLoading && (!!errorStatus || (!client && !!error) || !client)

  const clientDisplayName = client
    ? [client.firstName, client.lastName]
        .filter(Boolean)
        .map(s => String(s).toUpperCase())
        .join(' ') || 'UNNAMED CLAIMANT'
    : ''

  const title = client ? (
    clientDisplayName
  ) : hasFatalError ? (
    is404 ? (
      'CASE NOT FOUND'
    ) : is403 ? (
      'ACCESS DENIED'
    ) : (
      'UNABLE TO LOAD CASE'
    )
  ) : (
    <div className="skeleton" style={{ width: '200px', height: '1.5em' }} />
  )

  const subtitle =
    client || hasFatalError ? `CASE #${caseNumber}` : <div className="skeleton mt-1" style={{ width: '120px' }} />

  return (
    <Slide className="container-case-page background" direction="up" in={true} mountOnEnter unmountOnExit>
      <div>
        <Helmet title={`Case ${caseNumber}${clientDisplayName ? ` - ${clientDisplayName}` : ''}`} />
        <div className="case-header-sticky-wrapper">
          <CaseNavigationBar
            title={title}
            subtitle={subtitle}
            rightButtonText={!hasFatalError && selectedTab && selectedTab.needsSave ? 'SAVE' : ''}
            handleClickLeft={handleClickExit}
            handleRightClick={triggerFormSubmission}
            isClosed={caseStatus === 'closed'}
            isDraft={caseStatus === 'draft'}
            isRightButtonDisabled={selectedTab && selectedTab.needsSave && !isDirty}
            hideCloseButton={true}
          />
          {!hasFatalError && (
            <div
              className={`tab-bar-container w-100 ${showLeftScrollFade ? 'show-left-fade' : ''} ${
                showRightScrollFade ? 'show-right-fade' : ''
              }`}
            >
              <div ref={tabBarScrollRef} className="tab-bar-scroll-wrapper" onScroll={updateScrollFades}>
                <ul className="container tab-bar">
                  {tabs.map((tab, index) => (
                    <li key={index} className={getTabClass(index)}>
                      {window.location.pathname === tab.link ? (
                        <span>{tab.name}</span>
                      ) : (
                        <Link
                          id={`${tab.name.toLowerCase()}Tab`}
                          to={tab.link}
                          style={isLoading ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                        >
                          {tab.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div
            className="tab-bar-content-container d-flex justify-content-center align-items-center p-5"
            style={{ minHeight: '300px' }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : hasFatalError ? (
          <div className="tab-bar-content-container">
            <div className="container py-5">
              <div
                className="card border-0 shadow-sm mx-auto text-center p-4 p-md-5"
                style={{ maxWidth: '580px', borderRadius: '12px', background: '#fff' }}
              >
                {is404 ? (
                  <>
                    <div className="mb-4">
                      <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light text-warning"
                        style={{ width: '80px', height: '80px', fontSize: '32px' }}
                      >
                        <FAIcon name="search" type="fas" />
                      </div>
                    </div>
                    <h3 className="font-weight-bold mb-3 text-dark">Case Not Found</h3>
                    <p className="text-muted mb-4" style={{ fontSize: '1.05rem', lineHeight: '1.6' }}>
                      Case <strong>#{caseNumber}</strong> could not be found. It may have been deleted, or the case
                      number in the URL may be incorrect.
                    </p>
                    <div className="d-flex justify-content-center flex-wrap gap-2">
                      <button className="btn btn-primary px-4 py-2" onClick={handleClickExit}>
                        <FAIcon name="arrow-left" type="fas" className="mr-2" /> Back to Cases
                      </button>
                    </div>
                  </>
                ) : is403 ? (
                  <>
                    <div className="mb-4">
                      <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light text-danger"
                        style={{ width: '80px', height: '80px', fontSize: '32px' }}
                      >
                        <FAIcon name="lock" type="fas" />
                      </div>
                    </div>
                    <h3 className="font-weight-bold mb-3 text-dark">Access Denied</h3>
                    <p className="text-muted mb-4" style={{ fontSize: '1.05rem', lineHeight: '1.6' }}>
                      You do not have permission to view Case <strong>#{caseNumber}</strong>. If you believe you should
                      have access, please contact your administrator.
                    </p>
                    <div className="d-flex justify-content-center flex-wrap gap-2">
                      <button className="btn btn-primary px-4 py-2" onClick={handleClickExit}>
                        <FAIcon name="arrow-left" type="fas" className="mr-2" /> Back to Cases
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light text-danger"
                        style={{ width: '80px', height: '80px', fontSize: '32px' }}
                      >
                        <FAIcon name="exclamation-triangle" type="fas" />
                      </div>
                    </div>
                    <h3 className="font-weight-bold mb-3 text-dark">Unable to Load Case</h3>
                    <p className="text-muted mb-4" style={{ fontSize: '1.05rem', lineHeight: '1.6' }}>
                      {error || 'An unexpected error occurred while loading this case.'}
                    </p>
                    <div className="d-flex justify-content-center flex-wrap gap-2">
                      <button
                        className="btn btn-outline-secondary px-4 py-2 mr-2"
                        onClick={() => window.location.reload()}
                      >
                        <FAIcon name="redo" type="fas" className="mr-2" /> Try Again
                      </button>
                      <button className="btn btn-primary px-4 py-2" onClick={handleClickExit}>
                        <FAIcon name="arrow-left" type="fas" className="mr-2" /> Back to Cases
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="tab-bar-content-container">
            {error && (
              <div className="container pt-3">
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
                  <button type="button" className="close" onClick={() => setError(null)} aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
            )}
            <ErrorBoundary>
              <Switch>
                {tabs.map(({ name, pathProps, component: Component }) => (
                  <Route key={name} {...pathProps}>
                    <Component
                      caseNumber={caseNumber}
                      user={user}
                      settings={settings}
                      showError={setError}
                      formRef={saveFormRef}
                      incompleteAppointments={incompleteAppointments}
                      allAppointments={allAppointments}
                      incompleteTasks={incompleteTasks}
                      completeTasks={completeTasks}
                      isCaseClosed={caseStatus === 'closed'}
                      isDraft={caseStatus === 'draft'}
                      allowedReportTypes={allowedReportTypes}
                      caseTypeKey={caseTypeKey}
                      disableFileReport={disableFileReport}
                      refreshCaseStatus={refreshCaseStatus}
                      onTaskDeleted={handleTaskDeleted}
                      onDirtyStateChange={setIsDirty}
                    />
                  </Route>
                ))}
              </Switch>
            </ErrorBoundary>
          </div>
        )}
      </div>
    </Slide>
  )
})

export default CasePage
